/**
 * PaymentServicesSettingsCtrl controller
 */

import moment from 'moment'
import {
  CURRENCY_CONVERTER_TYPES,
  PAYMENT_SYSTEM_TYPES,
  SUBSCRIPTION_TYPES
} from 'app/core/constants'
import { find } from 'app/helpers/object'
import { remove } from 'app/helpers/entity'

// controller mixins
PaymentServicesSettingsCtrl.mixes = [
  'onOffCtrlMixin'
]

/**
 * PaymentServicesSettingsCtrl controller
 *
 * @class PaymentServicesSettingsCtrl
 * @memberof mwApp/packages
 * @see mwApp/packages
 * @mixes mwApp/common.mixin:onOffCtrlMixin
 *
 * @ngdoc controller
 * @name mwApp/packages.controller:PaymentServicesSettingsCtrl
 */
/* @ngInject */
export default function PaymentServicesSettingsCtrl ($location, $scope, $uibModal, Subscription, NetworkPaymentSystem, NetworkCurrency, NetworkSetting, NetworkCurrencyConverter, pckg, inapp, ecwid, authSvc, alertSvc, generalSettings) {
  var vm = this
  vm.mixes = [['tabsCtrlMixin', '', 'currencies', 'currency_converter']]

  /**
   * Platforms array
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @type {Object[]}
   */
  vm.platforms = pckg._enhancePlatforms().map(platform => platform.name)

  /**
   * Has access channel constant
   *
   * @memberOf mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @function
   */
  vm.hasAccess = function (access) {
    return authSvc.hasAccess(access)
  }

  /**
   * Loader indicator
   *
   * @type {boolean}
   */
  $scope.loading = true

  /**
   * Promise stack
   *
   * @type {Array}
   */
  vm.loaderStack = []

  /**
   * Package/Network
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @type {models.Network}
   */
  vm.pckg = pckg

  /**
   * Default Package for in-app registration
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @type {number}
   */
  vm.inappNetworkId = !inapp?.multiorgEnabled && inapp?.inAppRegistrationSettings?.enabled && inapp.inAppRegistrationSettings.network?.id

  /**
   * Default Package for Guest Mode
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @type {number}
   */
  vm.guestNetworkId = !inapp?.multiorgEnabled && inapp?.guestModeSettings?.enabled && inapp.guestModeSettings.network?.id

  /**
   * Default Subscription for in-app registration
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @type {number}
   */
  vm.inappSubscriptionId = !inapp?.multiorgEnabled && inapp?.inAppRegistrationSettings?.enabled && inapp.inAppRegistrationSettings.subscription?.id

  /**
   * Default Package for Guest Mode
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @type {number}
   */
  vm.guestSubscriptionId = !inapp?.multiorgEnabled && inapp?.guestModeSettings?.enabled && inapp.guestModeSettings.subscription?.id
  /*********************************************************************************************************************************/
  /* === Payment Sets                                                                                                          === */
  /*********************************************************************************************************************************/

  /**
   * Subscription types
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @type {Object}
   */
  vm.subscription_types = angular.copy(SUBSCRIPTION_TYPES)

  if (authSvc.hasEnabledFlag('ECOMMERCE_PROVIDER_ENABLED')) {
    vm.pckg.$ecwidEnabled = ecwid ? !!ecwid.enabled : false
  }

  if (!vm.pckg.$ecwidEnabled) {
    delete vm.subscription_types['Subscription ECWID']
  }

  /**
   * Subscriptions list
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @name vm.subscriptions
   * @type {models.Subscription[]}
   */

  // get subscriptions
  vm.loaderStack.push(Subscription.loadAll({ networkId: pckg.id })
    .to(vm.subscriptions = []).then(() => {
      vm.subscriptions.forEach(sub => {
        if (sub.trial) {
          sub.$trialLabel = 'Allowed'
        } else if (sub.subscriptionType === 'Subscription') {
          sub.$trialLabel = 'No'
        } else {
          sub.$trialLabel = '--'
        }
        if (sub.subscriptionType !== 'Subscription') {
          sub.platforms = []
        } else if (sub.useNetworkPlatforms) {
          sub.platforms = pckg.platforms
        }
      })
    }))

  /**
   * Delete subscription
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @param  {models.Subscription} subscription
   */
  vm.deleteSubscription = function (subscription) {
    subscription._delete()
      .then(remove(vm.subscriptions)(subscription))
  }

  /*********************************************************************************************************************************/
  /* === Payment Services                                                                                                      === */
  /*********************************************************************************************************************************/

  /**
   * Payment system types
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @type {Object}
   */
  vm.payment_system_types = PAYMENT_SYSTEM_TYPES

  /**
   * NetworkPaymentSystem services list
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @name vm.payments
   * @type {models.NetworkPaymentSystem[]}
   */

  // current default payment
  var defaultPaymentSystem = null

  // load payment systems
  vm.loaderStack.push(NetworkPaymentSystem.loadAll({ network: pckg })
    .to(vm.payments = [])
    .then(
      () => {
        vm.payments = vm.payments.filter(p => !['TELR', 'NEWEBPAY'].includes(p.paymentSystemType))
        vm.payments.forEach((p) => {
          p.$network = pckg // set fake field `$network` to use it in links
          p._updateUrl() // and update url for every payment system
        })
        defaultPaymentSystem = find(p => p.defaultPaymentSystem)(vm.payments) || vm.payments[0]
      }
    ))

  /**
   * Toggle payment and reload current network
   *
   * @param {models.NetworkPaymentSystem} payment
   */
  vm.onOffPayment = function (payment) {
    return vm.onOff(payment).then(() => {
      return pckg._update()
    })
  }

  /**
   * Check loader
   */
  Promise.all(vm.loaderStack)
    .then(() => { $scope.loading = false })

  /**
   * Set payment as default
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @param  {models.NetworkPaymentSystem} payment
   * @param setDefault
   */
  vm.setDefault = function (payment, setDefault) {
    payment._default(setDefault)
      .then(
        () => {
          if (defaultPaymentSystem && payment !== defaultPaymentSystem) {
            defaultPaymentSystem.defaultPaymentSystem = false
          }
          defaultPaymentSystem = payment
        }
      )
  }

  /**
   * Set payment as default
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @param  {models.NetworkPaymentSystem} payment
   */
  vm.systemDefaultPayment = function (payment) {
    payment._systemDefault()
  }

  /**
   * Get payment controller & template url by type
   *
   * @param type
   * @returns {{template: string, controller: string}}
   */
  const getPaymentAttributes = (type) => ({
    CASH: {
      template: 'components/settings/views/cashSettings.html',
      controller: 'AddCashPaymentServiceCtrl as vm'
    },
    EXTERNAL_PAYMENTS: {
      template: 'components/settings/views/externalPaymentSettings.html',
      controller: 'AddExternalPaymentServiceCtrl as vm'
    },
    AUTHORIZE_NET: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    PAYPAL: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    TELR: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    NEWEBPAY: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    GR4VY_GATEWAY: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    CCPP: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    TRUE_MONEY: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    TRUE_ONLINE: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    TRUE_MOVE_H: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    AIRWALLEX: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    },
    GOCARDLESS: {
      template: 'components/settings/views/addPaymentService.html',
      controller: 'AddPaymentServiceCtrl as vm'
    }
  }[type])

  /**
   * Edit payment service
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   * @param  {models.NetworkPaymentSystem} payment
   */
  vm.editPayment = function (payment) {
    const { template, controller } = getPaymentAttributes(payment.paymentSystemType)

    $uibModal.open({
      templateUrl: template,
      controller: controller,
      animation: false,
      size: 'lg',
      resolve: {
        payment: () => NetworkPaymentSystem.load(payment).then(data => Object.assign(data, { $fromLocation: 'NetworkPayment' }))
      }
    }).result
      .then(
        (p) => {
          // only field that could be changed in form, that is shown in the list -> update it
          payment.live = p.live
        }
      )
  }

  /*********************************************************************************************************************************/
  /* === Currencties                                                                                                           === */
  /*********************************************************************************************************************************/

  /**
   * Currencies list
   *
   * @memberof mwApp/settings.controller:PaymentSystemCtrl
   * @name vm.currencies
   * @type {models.NetworkCurrency[]}
   */

  // load currencies
  NetworkCurrency.loadAll({ network: pckg })
    .to(vm.currencies = [])

  /*********************************************************************************************************************************/
  /* === Network Setting                                                                                                       === */
  /*********************************************************************************************************************************/
  function prepareSettings () {
    // save settings in order to compare later what was changed
    vm.originals = angular.copy(vm.settingsCached)
    vm.paymentVoidEnabled = (vm.settingsCached.PAYMENT_VOID_HOURS.useSystemDefault ? generalSettings : vm.settingsCached).PAYMENT_VOID_HOURS.value !== 0
  }

  // load settings
  NetworkSetting.loadAll({ network: pckg })
    .to(vm.settingsCached = {})
    .then(prepareSettings)
    .then(() => {
      vm.networkSettings = angular.copy(vm.settingsCached)
      for (const setting in vm.networkSettings) {
        vm.changeSetting(setting)
      }
    })

  vm.changeSetting = function (param, changeVal = false) {
    const setting = vm.networkSettings[param]

    if (changeVal) {
      vm.settingsCached[param].value = setting.value
    } else {
      vm.settingsCached[param].useSystemDefault = setting.useSystemDefault
      setting.value = (setting.useSystemDefault ? generalSettings : vm.settingsCached)[param].value
    }
  }

  vm.changeUnlimited = function (param) {
    vm.changeSetting(param)
    vm.unlimitedActivationLength = vm.networkSettings[param].useSystemDefault
  }

  vm.changeDisallowVoid = function () {
    if (!vm.paymentVoidEnabled) {
      vm.networkSettings.PAYMENT_VOID_HOURS.value = 0
    } else {
      vm.changeSetting('PAYMENT_VOID_HOURS')
    }
  }

  vm.changeDisallowVoidUseSystem = function (param) {
    vm.changeSetting(param)
    vm.paymentVoidEnabled = !!(vm.networkSettings[param].useSystemDefault ? generalSettings : vm.networkSettings)[param].value
  }

  /**
   * Update settings
   *
   * @memberof mwApp/settings.controller:PaymentServicesSettingsCtrl
   */
  vm.submit = function () {
    vm.setDisable()

    if (!vm.paymentVoidEnabled) {
      vm.settingsCached.PAYMENT_VOID_HOURS.value = 0
    }

    NetworkSetting.saveAll(vm.settingsCached, vm.originals, { network: pckg })
      .then(() => {
        prepareSettings()
        alertSvc.alert('Settings updated', 'success')
      })
      .finally(vm.unsetDisable)
  }

  /*********************************************************************************************************************************/
  /* === Currency Converter                                                                                                    === */
  /*********************************************************************************************************************************/

  /**
   * Currency converters types
   *
   * @memberof mwApp/settings.controller:PaymentServicesSettingsCtrl
   * @type {Object}
   */
  vm.currency_converter_types = CURRENCY_CONVERTER_TYPES

  /**
   * Get NetworkCurrencyConverter list
   *
   * @memberof mwApp/settings.controller:PaymentServicesSettingsCtrl
   * @name vm.converters
   * @type {models.PaymentSystem[]}
   */

  // default currency converter
  let defaultConverter = null
  // load currency converters
  NetworkCurrencyConverter.loadAll({ $network: pckg }) // use fake field `$network`
    .to(vm.converters = [])
    .then(
      () => {
        vm.converters.forEach((c) => {
          c.$network = pckg // set fake field `$network` to use it in links
          c._updateUrl() // and update url for every payment system
        })
        defaultConverter = find(c => c.defaultConverter)(vm.converters) || vm.converters[0]
      }
    )

  /**
   * Format currency converter rate
   *
   * @memberof mwApp/settings.controller:PaymentServicesSettingsCtrl
   * @function
   * @param  {Number} seconds refresh rate
   */
  vm.formatRefreshRate = (seconds) => {
    if (seconds) {
      const duration = moment.duration(seconds * 60, 'seconds') // multiply by 60, because in the `refreshRateSec` field already minutes
      const [h, m, s] = [duration.hours(), duration.minutes(), duration.seconds()]
      return (h ? `${h} hours ` : '') + (m ? `${m} mins ` : '') + (s ? `${s} seconds` : '')
    }
    return 'Live'
  }

  /**
   * Edit currency converter
   *
   * @memberof mwApp/settings.controller:PaymentServicesSettingsCtrl
   * @function
   * @param  {models.NetworkCurrencyConverter} converter
   */
  vm.editConverter = function (converter) {
    $uibModal.open({
      templateUrl: 'components/settings/views/addCurrencyConverter.html',
      controller: 'AddCurrencyConverterCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        converter: () => Object.assign(converter, { $fromLocation: 'NetworkPayment' })
      }
    })
  }

  vm.onOffCurrency = function (currency) {
    const msg = {
      text: `Payment Sets with selected currency ${currency.name} will not be available for making Payments</br></br>` +
        `Making payments with Payment Services with allowed currency ${currency.name} might not be available if any other currency is not set</br></br></br>` +
        `Are you sure you want to disable currency ${currency.name} for Organization?`,
      header: 'Disable Currency'
    }
    if (!currency.enabled) {
      alertSvc.confirm(msg).then(() => {
        vm.onOff(currency)
      })
        .catch(() => {
          currency.enabled = true
        })
    } else {
      vm.onOff(currency)
    }
  }

  /**
   * Set converter as default
   *
   * @memberof mwApp/settings.controller:PaymentServicesSettingsCtrl
   * @param  {models.NetworkCurrencyConverter} converter
   * @param setDefault
   */
  vm.setDefaultConverter = function (converter, setDefault) {
    converter._default(setDefault)
      .then(
        () => {
          if (defaultConverter && converter !== defaultConverter) {
            defaultConverter.defaultConverter = false
          }
          defaultConverter = converter
        }
      )
  }

  /**
   * Set system default
   *
   * @memberof mwApp/settings.controller:PaymentServicesSettingsCtrl
   * @param  {models.NetworkCurrencyConverter} converter
   */
  vm.systemDefaultConverter = function (converter) {
    converter._systemDefault()
  }

  /**
   * redirection to 'generalsettings'
   *
   * @memberof mwApp/packages.controller:PaymentServicesSettingsCtrl
   */
  vm.redirectToSettings = function () {
    $location.url('generalsettings')
  }

  vm.formatSubscriptionType = function (subscription) {
    if (subscription.ecommerceEnabled) {
      return 'Subscription ECWID'
    }
    return subscription.subscriptionType
  }
}
